import React, { useState } from 'react';
import { Button, Dropdown, Form, InputGroup, Modal, OverlayTrigger, Popover } from 'react-bootstrap';
import { SortIcon, DropdownDots } from '../../../components/ui/icons';
import { ITemplate } from '../../../common/types/templateTypes';
import { resolveTemplateName } from '../../../common/templateColorVariants';
import { ICapEndpointRow } from './capTypes';

const MAX_VISIBLE_GROUPS = 2;

const GroupsCell: React.FC<{ groups: { id?: string; displayName?: string }[] }> = ({
  groups = [],
}) => {
  const [showPopover, setShowPopover] = useState(false);
  const hideTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  React.useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
      }
    };
  }, []);

  if (!groups.length) {
    return <div className="cap-groups-cell">—</div>;
  }

  const visibleGroups = groups.slice(0, MAX_VISIBLE_GROUPS);
  const remainingCount = groups.length - visibleGroups.length;
  const tooltipId = `cap-groups-${groups.map((g) => g.id || g.displayName).join('-')}`;

  const clearHideTimeout = () => {
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    clearHideTimeout();
    setShowPopover(true);
  };

  const handleMouseLeave = () => {
    clearHideTimeout();
    hideTimeoutRef.current = setTimeout(() => {
      setShowPopover(false);
    }, 150);
  };

  const groupsPopover = (
    <Popover
      id={tooltipId}
      className="cap-groups-popover"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      <Popover.Header as="div" className="cap-groups-popover-header">
        Assigned Groups ({groups.length})
      </Popover.Header>
      <Popover.Body className="cap-groups-popover-body">
        <ul>
          {groups.map((group) => (
            <li key={group.id || group.displayName}>{group.displayName}</li>
          ))}
        </ul>
      </Popover.Body>
    </Popover>
  );

  return (
    <div className="cap-groups-cell">
      {visibleGroups.map((group) => (
        <span key={group.id || group.displayName}>{group.displayName}</span>
      ))}
      {remainingCount > 0 && (
        <OverlayTrigger placement="top" show={showPopover} overlay={groupsPopover}>
          <span
            className="cap-groups-more"
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
          >
            +{remainingCount} more
          </span>
        </OverlayTrigger>
      )}
    </div>
  );
};

interface IDeleteCapEndpointModalProps {
  id: string;
  name: string;
  onDelete: (id: string) => void;
}

const DeleteCapEndpointModal: React.FC<IDeleteCapEndpointModalProps> = ({ id, name, onDelete }) => {
  const [show, setShow] = useState(false);
  return (
    <>
      <div onClick={() => setShow(true)}>Delete</div>
      <Modal show={show} onHide={() => setShow(false)} className="modal-container disable-modal" centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete CAP Endpoint</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>
            Are you sure you want to delete <strong>{name}</strong>? Any CAP producer still posting to
            this URL will start receiving 401 errors.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button className="btn-cancel" onClick={() => setShow(false)}>
            Cancel
          </Button>
          <Button
            className="btn-delete"
            onClick={() => {
              onDelete(id);
              setShow(false);
            }}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

interface IRegenerateCapTokenModalProps {
  id: string;
  name: string;
  onRegenerate: (id: string) => Promise<string | null>;
}

const RegenerateCapTokenModal: React.FC<IRegenerateCapTokenModalProps> = ({ id, name, onRegenerate }) => {
  const [show, setShow] = useState(false);
  const [newUrl, setNewUrl] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);

  const handleRegenerate = async () => {
    setIsRegenerating(true);
    try {
      const url = await onRegenerate(id);
      if (url) {
        setNewUrl(url);
      } else {
        setShow(false);
      }
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleClose = () => {
    setShow(false);
    setNewUrl('');
  };

  return (
    <>
      <div onClick={() => setShow(true)}>Regenerate Token</div>
      <Modal show={show} onHide={handleClose} className="modal-container disable-modal" centered>
        <Modal.Header closeButton>
          <Modal.Title>{newUrl ? 'New Webhook URL' : 'Regenerate Token'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {newUrl ? (
            <>
              <p>
                Token regenerated for <strong>{name}</strong>. Update the URL in your CAP producer
                configuration. The previous URL is no longer valid.
              </p>
              <InputGroup>
                <Form.Control value={newUrl} readOnly />
                <Button variant="outline-secondary" onClick={() => navigator.clipboard.writeText(newUrl)}>
                  Copy
                </Button>
              </InputGroup>
            </>
          ) : (
            <p>
              Regenerate the token for <strong>{name}</strong>? The current webhook URL will stop
              working immediately. You will need to update the URL in Rave or your CAP producer.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          {newUrl ? (
            <Button className="btn-cancel" onClick={handleClose}>
              Done
            </Button>
          ) : (
            <>
              <Button className="btn-cancel" onClick={handleClose}>
                Cancel
              </Button>
              <Button className="btn-delete" onClick={handleRegenerate} disabled={isRegenerating}>
                {isRegenerating ? 'Regenerating...' : 'Regenerate'}
              </Button>
            </>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
};

const CapColumns = (
  handleCopyUrl: (endpointToken: string, id: string) => void,
  copiedId: string | null,
  handleToggleActive: (endpointId: string, isActive: boolean) => void,
  handleDelete: (endpointId: string) => void,
  handleRegenerateToken: (endpointId: string) => Promise<string | null>,
  handleEdit: (endpoint: ICapEndpointRow) => void,
  templatesById: Map<number, ITemplate>
) => {
  return [
    {
      accessorFn: (row) => `${row.name}`,
      accessorKey: 'name',
      header: () => (
        <div className="table-heading">
          Name
          <span>
            <SortIcon />
          </span>
        </div>
      ),
      cell: (row) => <div className="table-cell">{row.getValue()}</div>,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'groups',
      header: () => <div className="table-heading">Groups</div>,
      cell: (row) => <GroupsCell groups={row.row.original.groups || []} />,
      enableColumnFilter: false,
    },
    {
      accessorKey: 'templateId',
      header: () => <div className="table-heading">Template</div>,
      cell: (row) => (
        <div className="table-cell">
          {resolveTemplateName(row.row.original.templateId, templatesById)}
        </div>
      ),
      enableColumnFilter: false,
    },
    {
      accessorKey: 'endpointToken',
      header: () => <div className="table-heading">Webhook URL</div>,
      cell: (row) => (
        <button
          type="button"
          className="cap-copy-url-btn"
          onClick={() => handleCopyUrl(row.row.original.endpointToken, row.row.original.id)}
        >
          {copiedId === row.row.original.id ? 'Copied!' : 'Copy URL'}
        </button>
      ),
      enableColumnFilter: false,
    },
    {
      accessorKey: 'isActive',
      header: () => <div className="table-heading">Status</div>,
      cell: (row) => (
        <Form.Check
          type="switch"
          checked={row.row.original.isActive}
          onChange={() => handleToggleActive(row.row.original.id, row.row.original.isActive)}
          label={row.row.original.isActive ? 'Active' : 'Inactive'}
        />
      ),
      enableColumnFilter: false,
    },
    {
      accessorKey: 'actions',
      header: () => <span></span>,
      cell: (row) => (
        <div className="actions-btns">
          <Dropdown>
            <Dropdown.Toggle id={`cap-dropdown-${row.row.original.id}`} variant="">
              <DropdownDots />
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item>
                <div onClick={() => handleEdit(row.row.original)}>Edit</div>
              </Dropdown.Item>
              <Dropdown.Item>
                <RegenerateCapTokenModal
                  id={row.row.original.id}
                  name={row.row.original.name}
                  onRegenerate={handleRegenerateToken}
                />
              </Dropdown.Item>
              <Dropdown.Item>
                <DeleteCapEndpointModal
                  id={row.row.original.id}
                  name={row.row.original.name}
                  onDelete={handleDelete}
                />
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>
      ),
      maxSize: 80,
      enableColumnFilter: false,
    },
  ];
};

export default CapColumns;
